import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ConfirmProvider } from './contexts/ConfirmProvider'
import { ExchangeProvider } from './contexts/ExchangeContext'
import { PermissionProvider } from './contexts/PermissionContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { BrandingProvider } from './contexts/BrandingProvider'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { AdminRoute } from './components/auth/AdminRoute'
import { OrgAdminRoute } from './components/auth/OrgAdminRoute'
import { PermissionGate } from './components/auth/PermissionGate'
import { AccessDeniedCard } from './components/ui/AccessDeniedCard'
import { PlatformAdminLayout } from './components/layout/AdminLayout'
import { OrgAdminLayout } from './components/layout/OrgAdminLayout'
import { RegularUserLayout } from './components/layout/RegularUserLayout'
import { ImpersonationBanner } from './components/admin/ImpersonationBanner'
import { HomePage } from './pages/HomePage'
import SplashPage from './pages/SplashPage'
import SplashV2Page from './pages/SplashV2Page'
import { StyleGuidePage } from './pages/StyleGuidePage'
import { MobilePreviewPage } from './pages/MobilePreviewPage'
import { LoginPage } from './pages/auth/LoginPage'
import { SlugLoginPage } from './pages/auth/SlugLoginPage'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage'
import { MfaChallengePage } from './pages/auth/MfaChallengePage'
import { DashboardPage } from './pages/DashboardPage'
import { DocumentsPage } from './pages/DocumentsPage'
import { CalculatorPage } from './pages/CalculatorPage'
import { MarketplacePage } from './pages/MarketplacePage'
import { FormsPage } from './pages/FormsPage'
import { ProfilePage } from './pages/ProfilePage'
import { UserManagementPage } from './pages/UserManagementPage'
import { ActivityPage } from './pages/ActivityPage'
import { AdminDashboard } from './pages/admin/AdminDashboard'
import { AdminUsersPage } from './pages/admin/AdminUsersPage'
import { AdminExchangesPage } from './pages/admin/AdminExchangesPage'
import { AdminDocumentsPage } from './pages/admin/AdminDocumentsPage'
import { AdminMarketplacePage } from './pages/admin/AdminMarketplacePage'
import { AdminMarketplaceEditor } from './pages/admin/AdminMarketplaceEditor'
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage'
import { AdminIntegrationsPage } from './pages/admin/AdminIntegrationsPage'
import { AdminBrandingPage } from './pages/admin/AdminBrandingPage'
import { AdminOrgsPage } from './pages/admin/AdminOrgsPage'
import { AdminOrgEditor } from './pages/admin/AdminOrgEditor'
import { NotFoundPage } from './pages/NotFoundPage'
import { CompanyPage } from './pages/CompanyPage'
import { ContactPage } from './pages/ContactPage'
import { FAQPage } from './pages/FAQPage'
import { FeaturesPage } from './pages/FeaturesPage'
import { PricingPage } from './pages/PricingPage'
import { SecurityPage } from './pages/SecurityPage'
import { DocumentationPage } from './pages/DocumentationPage'
import { GettingStartedPage } from './pages/docs/GettingStartedPage'
import { ApiDocumentationPage } from './pages/docs/ApiDocumentationPage'
import { VideoTutorialsPage } from './pages/docs/VideoTutorialsPage'
import { ExchangeProcessPage } from './pages/docs/ExchangeProcessPage'
import { BestPracticesPage } from './pages/docs/BestPracticesPage'
import { TroubleshootingPage } from './pages/docs/TroubleshootingPage'
import { SupportPage } from './pages/SupportPage'
import { BlogPage } from './pages/BlogPage'
import { CareersPage } from './pages/CareersPage'
import { PartnersPage } from './pages/PartnersPage'
import { CompliancePage } from './pages/CompliancePage'
import { ResourcesPage } from './pages/ResourcesPage'
import { LegalPage } from './pages/LegalPage'
import { AuthModalHost } from './components/auth/AuthModalHost'
import { PublicChatLauncher } from './components/chat/PublicChatLauncher'
import { Favicon } from './components/ui/Favicon'
import { ChatProvider } from './contexts/ChatContext'
import { useAuth } from './contexts/AuthContext'
import { useLocation } from 'react-router-dom'
import { ErrorBoundary } from './components/ErrorBoundary'

function AppContent() {
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  // Show public chat launcher only on public routes when not authenticated
  // Excludes branded slug routes (/o/:slug/...) so customer-facing pages stay clean
  const isPublicRoute = !location.pathname.startsWith('/app')
  const isBrandedRoute = location.pathname.startsWith('/o/')
  const shouldShowPublicChat = isPublicRoute && !isBrandedRoute && !isAuthenticated

  return (
    <div className="relative">
      <ImpersonationBanner />
      <Favicon />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/splash" element={<SplashPage />} />
        <Route path="/splash-v2" element={<SplashV2Page />} />
        <Route path="/styleguide" element={<AdminRoute><StyleGuidePage /></AdminRoute>} />
        <Route path="/mobile-preview" element={<MobilePreviewPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/o/:slug/login" element={<SlugLoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/o/:slug/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/login/mfa" element={<MfaChallengePage />} />
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/documentation" element={<DocumentationPage />} />
          <Route path="/documentation/getting-started" element={<GettingStartedPage />} />
          <Route path="/documentation/api" element={<ApiDocumentationPage />} />
          <Route path="/documentation/video-tutorials" element={<VideoTutorialsPage />} />
          <Route path="/documentation/exchange-process" element={<ExchangeProcessPage />} />
          <Route path="/documentation/best-practices" element={<BestPracticesPage />} />
          <Route path="/documentation/troubleshooting" element={<TroubleshootingPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/legal" element={<LegalPage />} />
        
        {/* Redirect legacy /dashboard URL to /app */}
        <Route path="/dashboard" element={<Navigate to="/app" replace />} />

        {/* Protected routes - Regular users (non-admin) — slug-prefixed variant */}
        <Route path="/o/:slug/app" element={
          <ProtectedRoute>
            <ErrorBoundary>
              <RegularUserLayout />
            </ErrorBoundary>
          </ProtectedRoute>
        }>
          <Route index element={<DashboardPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="documents" element={
            <PermissionGate feature="documents" fallback={<AccessDeniedCard feature="Documents" />}>
              <DocumentsPage />
            </PermissionGate>
          } />
          <Route path="calculator" element={
            <PermissionGate feature="calculator" fallback={<AccessDeniedCard feature="Calculator" />}>
              <CalculatorPage />
            </PermissionGate>
          } />
          <Route path="marketplace" element={
            <PermissionGate feature="marketplace" fallback={<AccessDeniedCard feature="Marketplace" />}>
              <MarketplacePage />
            </PermissionGate>
          } />
          <Route path="marketplace/:id" element={
            <PermissionGate feature="marketplace" fallback={<AccessDeniedCard feature="Marketplace" />}>
              <MarketplacePage />
            </PermissionGate>
          } />
          <Route path="forms" element={
            <PermissionGate feature="irs_forms" fallback={<AccessDeniedCard feature="IRS Forms" />}>
              <FormsPage />
            </PermissionGate>
          } />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="users" element={<UserManagementPage />} />
          <Route path="activity" element={<ActivityPage />} />
        </Route>

        {/* Protected routes - Regular users (non-admin) */}
        <Route path="/app" element={
          <ProtectedRoute>
            <ErrorBoundary>
              <RegularUserLayout />
            </ErrorBoundary>
          </ProtectedRoute>
        }>
          <Route index element={<DashboardPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="documents" element={
            <PermissionGate feature="documents" fallback={<AccessDeniedCard feature="Documents" />}>
              <DocumentsPage />
            </PermissionGate>
          } />
          <Route path="calculator" element={
            <PermissionGate feature="calculator" fallback={<AccessDeniedCard feature="Calculator" />}>
              <CalculatorPage />
            </PermissionGate>
          } />
          <Route path="marketplace" element={
            <PermissionGate feature="marketplace" fallback={<AccessDeniedCard feature="Marketplace" />}>
              <MarketplacePage />
            </PermissionGate>
          } />
          <Route path="marketplace/:id" element={
            <PermissionGate feature="marketplace" fallback={<AccessDeniedCard feature="Marketplace" />}>
              <MarketplacePage />
            </PermissionGate>
          } />
          <Route path="forms" element={
            <PermissionGate feature="irs_forms" fallback={<AccessDeniedCard feature="IRS Forms" />}>
              <FormsPage />
            </PermissionGate>
          } />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="users" element={<UserManagementPage />} />
          <Route path="activity" element={<ActivityPage />} />
        </Route>

        {/* Platform admin routes */}
        <Route path="/app/admin" element={
          <AdminRoute>
            <ErrorBoundary>
              <PlatformAdminLayout />
            </ErrorBoundary>
          </AdminRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="exchanges" element={<AdminExchangesPage />} />
          <Route path="documents" element={<AdminDocumentsPage />} />
          <Route path="marketplace" element={<AdminMarketplacePage />} />
          <Route path="marketplace/:id/edit" element={<AdminMarketplaceEditor />} />
          <Route path="marketplace/new" element={<AdminMarketplaceEditor />} />
          <Route path="integrations" element={<AdminIntegrationsPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="orgs" element={<AdminOrgsPage />} />
          <Route path="orgs/:orgId/edit" element={<AdminOrgEditor />} />
          {/* TODO: remove after one release cycle — redirecting old demo-tenants URL to unified orgs page */}
          <Route path="demo-tenants" element={<Navigate to="/app/admin/orgs?filter=demos" replace />} />
        </Route>

        {/* Org admin routes */}
        <Route path="/app/org/:orgId/admin" element={
          <OrgAdminRoute>
            <ErrorBoundary>
              <OrgAdminLayout />
            </ErrorBoundary>
          </OrgAdminRoute>
        }>
          <Route path="branding" element={<AdminBrandingPage />} />
        </Route>
        
        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Global auth modal (desktop only, opens when not authenticated) */}
      <AuthModalHost />
      
      {/* Public chat launcher - shows on all public pages when not authenticated */}
      {shouldShowPublicChat && <PublicChatLauncher />}
    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ConfirmProvider>
          <BrandingProvider>
            <ExchangeProvider>
              <PermissionProvider>
                <ChatProvider>
                  <AppContent />
                </ChatProvider>
              </PermissionProvider>
            </ExchangeProvider>
          </BrandingProvider>
        </ConfirmProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
